<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { prefersReducedMotion } from '../../utils/environment'
import { LX_BIDS, STEPS } from './constants'

const CARD_W = 1038
const GAP = 120
const AUTO_MS = 4000

const activeIndex = ref(0)
const stageRef = ref<HTMLElement | null>(null)
const stageWidth = ref(1440)
const paused = ref(false)
/** 首尾无缝跳转时关闭过渡，避免整轨回弹滑动 */
const skipTransition = ref(false)

const current = computed(() => STEPS[activeIndex.value])
const stepCount = STEPS.length
const prevIndex = computed(() => (activeIndex.value - 1 + stepCount) % stepCount)
const nextIndex = computed(() => (activeIndex.value + 1) % stepCount)

let timer: ReturnType<typeof setInterval> | null = null

const measure = () => {
  stageWidth.value = stageRef.value?.clientWidth || window.innerWidth
}

const clearAutoplay = () => {
  if (!timer) return
  clearInterval(timer)
  timer = null
}

const setActive = (next: number) => {
  const from = activeIndex.value
  const wrapping =
    stepCount > 1 &&
    ((from === stepCount - 1 && next === 0) || (from === 0 && next === stepCount - 1))

  if (wrapping) {
    skipTransition.value = true
    activeIndex.value = next
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        skipTransition.value = false
      })
    })
    return
  }

  activeIndex.value = next
}

const advance = () => {
  if (stepCount <= 1) return
  setActive((activeIndex.value + 1) % stepCount)
}

const startAutoplay = () => {
  clearAutoplay()
  if (prefersReducedMotion() || paused.value || stepCount <= 1) return
  timer = setInterval(() => {
    if (paused.value) return
    advance()
  }, AUTO_MS)
}

const restartAutoplay = () => {
  startAutoplay()
}

onMounted(() => {
  measure()
  window.addEventListener('resize', measure, { passive: true })
  startAutoplay()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', measure)
  clearAutoplay()
})

const getActiveLeft = (width: number) => Math.max(0, (width - CARD_W) / 2)

const railStyle = computed(() => {
  const index = activeIndex.value
  const activeLeft = getActiveLeft(stageWidth.value)
  const offset = activeLeft - index * (CARD_W + GAP)
  return {
    transform: `translate3d(${offset}px, 0, 0)`
  }
})

/** 左右漏出区域宽度，用于把箭头居中在侧栏内容正中 */
const peekWidths = computed(() => {
  const w = stageWidth.value
  const activeLeft = getActiveLeft(w)
  return {
    prev: Math.max(0, activeLeft - GAP),
    next: Math.max(0, w - (activeLeft + CARD_W + GAP))
  }
})

const prevArrowStyle = computed(() => ({
  left: `${peekWidths.value.prev / 2}px`
}))

const nextArrowStyle = computed(() => ({
  right: `${peekWidths.value.next / 2}px`
}))

const goPrev = () => {
  setActive(prevIndex.value)
  restartAutoplay()
}

const goNext = () => {
  setActive(nextIndex.value)
  restartAutoplay()
}

const onStageEnter = () => {
  paused.value = true
}

const onStageLeave = () => {
  paused.value = false
}
</script>

<template>
  <section
    id="stepSection"
    class="steps-section"
    :data-mv-bid="LX_BIDS.stepMv"
    data-mv-key="steps"
    :data-mv-module-identifier="`${activeIndex + 1}`"
  >
    <div class="step-heading">
      <div class="step-heading-title">
        <span class="step-heading-num">{{ current.num }}</span>
        <span class="step-heading-name">{{ current.name }}</span>
      </div>
      <div class="step-heading-sub">{{ current.subtitle }}</div>
    </div>

    <div
      ref="stageRef"
      class="step-stage"
      @mouseenter="onStageEnter"
      @mouseleave="onStageLeave"
    >
      <div
        class="step-rail"
        :class="{ 'is-jumping': skipTransition }"
        :style="railStyle"
      >
        <article
          v-for="(step, index) in STEPS"
          :key="step.num"
          class="step-card"
          :class="{
            'is-active': index === activeIndex,
            'is-prev': index === prevIndex,
            'is-next': index === nextIndex
          }"
        >
          <div class="step-image">
            <img :src="step.image" :alt="`${step.name}界面`">
          </div>
        </article>
      </div>

      <!-- 无限循环：箭头挂在舞台层，首尾也能切到对侧 -->
      <button
        class="step-nav step-nav-prev"
        type="button"
        aria-label="上一步"
        :style="prevArrowStyle"
        @click="goPrev"
      >
        <span class="step-arrow-icon" />
      </button>
      <button
        class="step-nav step-nav-next"
        type="button"
        aria-label="下一步"
        :style="nextArrowStyle"
        @click="goNext"
      >
        <span class="step-arrow-icon" />
      </button>
    </div>
  </section>
</template>

<style scoped lang="scss">
.steps-section {
  margin: 160px 0 0;
  padding: 0 0 40px;
  overflow: hidden;
}

.step-heading {
  width: 1200px;
  max-width: calc(100% - 48px);
  margin: 0 auto;
  text-align: center;
}

.step-heading-title {
  display: inline-flex;
  align-items: center;
  gap: 21px;
  font-size: 48px;
  font-weight: 700;
  line-height: normal;
}

.step-heading-num {
  color: #171717;
}

.step-heading-name {
  color: #8932ff;
}

.step-heading-sub {
  margin-top: 24px;
  color: #666;
  font-size: 18px;
  font-weight: 300; /* L */
  line-height: 1.5;
}

.step-stage {
  position: relative;
  width: 100%;
  margin-top: 56px;
  /* 上下留出投影空间，避免被 overflow 裁切成硬边 */
  padding: 28px 0 48px;
  overflow: hidden;
}

.step-rail {
  display: flex;
  gap: 120px;
  will-change: transform;
  transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

.step-rail.is-jumping {
  transition: none;
}

.step-card {
  position: relative;
  flex: none;
  width: 1038px;
  height: 524px;
  /* 阴影偏下，顶部只留很淡的弥散，避免上沿过实 */
  box-shadow:
    0 2px 12px rgba(77, 76, 77, 0.05),
    0 18px 40px rgba(50, 50, 50, 0.08);
}

/* 相邻步骤只保留导航箭头，避免图片和阴影形成大块矩形背景。 */
.step-card.is-prev,
.step-card.is-next {
  box-shadow: none;
}

.step-card.is-prev .step-image,
.step-card.is-next .step-image {
  visibility: hidden;
}

.step-image {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #f0f0f0;
}

.step-image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.step-nav {
  position: absolute;
  z-index: 2;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.08),
    0 8px 24px rgba(0, 0, 0, 0.14);
  transform: translate(-50%, -50%);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.step-nav-next {
  transform: translate(50%, -50%);
}

.step-arrow-icon {
  display: block;
  width: 10px;
  height: 10px;
  border-right: 2px solid #171717;
  border-bottom: 2px solid #171717;
}

.step-nav-prev .step-arrow-icon {
  margin-left: 3px;
  transform: rotate(135deg);
}

.step-nav-next .step-arrow-icon {
  margin-right: 3px;
  transform: rotate(-45deg);
}

@media (max-width: 960px) {
  .steps-section {
    margin: 64px 0 0;
    padding: 0 0 24px;
  }

  .step-heading {
    width: auto;
    max-width: none;
    padding: 0 20px;
  }

  .step-heading-title {
    gap: 10px;
    font-size: 28px;
  }

  .step-heading-sub {
    margin-top: 12px;
    font-size: 16px;
  }

  .step-stage {
    margin-top: 24px;
    padding: 8px 20px 40px;
    overflow: visible;
  }

  .step-rail {
    gap: 16px;
    transform: none !important;
  }

  .step-card {
    display: none;
    width: 100%;
    height: auto;
  }

  .step-card.is-active {
    display: block;
  }

  .step-card.is-prev,
  .step-card.is-next {
    display: none;
  }

  .step-image {
    height: auto;
    aspect-ratio: 1038 / 524;
  }

  .step-nav {
    top: calc(8px + (100% - 48px) / 2);
    width: 36px;
    height: 36px;
  }

  .step-nav-prev {
    left: 28px !important;
    transform: translate(0, -50%);
  }

  .step-nav-next {
    right: 28px !important;
    left: auto !important;
    transform: translate(0, -50%);
  }
}
</style>
